import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BookOpen, Check } from "lucide-react";
import { scrollToId } from "@/config";

const lines = [
  { text: "Autismo", cls: "" },
  { text: "e seus cuidados", cls: "italic text-brand" },
  { text: "especiais.", cls: "text-flame" },
];

const trust = ["Linguagem acessível", "Orientações práticas", "Checklist + plano de apoio"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="sobre" className="relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-20 pt-16 md:grid-cols-[1.05fr_0.95fr] md:px-12 md:pb-28 md:pt-24">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brandlight px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-brand"
            data-testid="hero-badge"
          >
            <BookOpen className="h-3.5 w-3.5" />
            E-book digital • 15 capítulos
          </motion.div>

          <h1 className="font-display text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl md:text-7xl">
            {lines.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ink sm:text-lg"
          >
            Um guia prático e afetivo para compreender o TEA, melhorar a comunicação,
            acolher necessidades e promover mais autonomia e inclusão no dia a dia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-buy-button"
              onClick={() => scrollToId("comprar")}
              className="group inline-flex items-center gap-2 rounded-full bg-flame px-8 py-4 text-sm font-extrabold uppercase tracking-wide text-white shadow-[0_16px_40px_-12px_rgba(234,88,12,0.55)] transition-all duration-200 hover:scale-105 hover:bg-[#c2410c]"
            >
              Quero o e-book
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-content-button"
              onClick={() => scrollToId("conteudo")}
              className="rounded-full border-2 border-navy/15 px-8 py-4 text-sm font-extrabold uppercase tracking-wide text-navy transition-all duration-200 hover:scale-105 hover:border-navy/40"
            >
              Ver o que tem dentro
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-9 flex flex-wrap gap-x-6 gap-y-2"
          >
            {trust.map((t) => (
              <span key={t} className="flex items-center gap-1.5 text-sm font-bold text-ink">
                <Check className="h-4 w-4 text-green-600" strokeWidth={3} />
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-[520px]"
          data-testid="hero-product"
        >
          <motion.div style={{ y: yBlob }} className="absolute -right-8 -top-8 h-44 w-44 rounded-full bg-sun/70 blur-xl" />
          <motion.div style={{ y: yBlob }} className="absolute -bottom-6 -left-10 h-40 w-40 rounded-full border-[10px] border-aqua/50" />
          <motion.div style={{ y: yImg }} className="relative">
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <img
                src="/produto-autismo.png"
                alt="E-book Autismo e Seus Cuidados Especiais"
                className="relative z-10 w-full rounded-3xl shadow-[0_40px_80px_-24px_rgba(10,25,47,0.35)]"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
